import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/kunlun-data';

const articleImages: Record<string, string> = {
  'article-glacier-health': 'https://readdy.ai/api/search-image?query=Elegant%20editorial%20photography%20of%20pure%20glacier%20water%20in%20a%20transparent%20glass%20on%20a%20clean%20white%20surface%20with%20natural%20sunlight%2C%20minimalist%20lifestyle%20aesthetic%2C%20soft%20shadows%2C%20premium%20wellness%20brand%20feel&width=800&height=450&seq=article-glacier-health&orientation=landscape',
  'article-brand-story': 'https://readdy.ai/api/search-image?query=Majestic%20Kunlun%20mountain%20range%20with%20ancient%20glaciers%20under%20golden%20sunrise%20light%2C%20pristine%20snow%20covered%20peaks%2C%20ethereal%20mist%20in%20deep%20valleys%2C%20cinematic%20aerial%20landscape&width=800&height=450&seq=article-brand-story&orientation=landscape',
  'article-guide': 'https://readdy.ai/api/search-image?query=A%20collection%20of%20different%20premium%20mineral%20water%20bottles%20arranged%20elegantly%20on%20a%20white%20marble%20surface%20with%20natural%20lighting%2C%20lifestyle%20product%20photography%2C%20clean%20composition&width=800&height=450&seq=article-guide&orientation=landscape',
  'article-culture': 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20ink%20painting%20style%20depicting%20the%20sacred%20Kunlun%20mountains%20with%20flowing%20waterfalls%20and%20ancient%20pine%20trees%2C%20misty%20atmosphere%2C%20ethereal%20spiritual%20aesthetic&width=800&height=450&seq=article-culture&orientation=landscape',
  'article-compare': 'https://readdy.ai/api/search-image?query=Two%20glasses%20of%20water%20side%20by%20side%20on%20a%20minimal%20white%20table%2C%20one%20with%20glacier%20ice%20cubes%2C%20scientific%20comparison%20aesthetic%2C%20clean%20laboratory%20style%20lighting&width=800&height=450&seq=article-compare&orientation=landscape',
  'article-awards': 'https://readdy.ai/api/search-image?query=A%20premium%20mineral%20water%20bottle%20next%20to%20three%20golden%20award%20medals%20on%20a%20white%20pedestal%20with%20dramatic%20spotlight%2C%20prestigious%20awards%20ceremony%20feel%2C%20elegant%20atmosphere&width=800&height=450&seq=article-awards&orientation=landscape',
};

const listSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '昆仑雪文章资讯',
  description: '浏览昆仑雪品牌故事、健康科普、选购指南和水文化等相关文章，了解高端矿泉水知识与品牌动态。',
};

export default function ArticlesPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <SEO
        title="文章资讯"
        description="浏览昆仑雪品牌故事、健康科普、选购指南和水文化等相关文章，了解昆仑山冰川水的健康价值与品牌动态。"
        keywords="昆仑雪文章,冰川水健康,品牌故事,矿泉水选购指南,昆仑山水文化"
        canonicalUrl="https://kunlunsnow.com/articles"
      />
      <script type="application/ld+json">{JSON.stringify(listSchema)}</script>
      <main>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '文章资讯' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-5 mb-2">
              文章资讯
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              探索昆仑雪的品牌故事、健康科普、选购指南与水文化，了解高端矿泉水的方方面面
            </p>
          </div>
        </section>

        <section className="pb-16 md:pb-24 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/articles/${article.slug}`}
                  className="group bg-background-100 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.01]"
                >
                  <div className="relative h-48 md:h-52 overflow-hidden">
                    <img
                      src={articleImages[article.imageSeq]}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="text-[10px] px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full font-medium whitespace-nowrap">
                        {article.category}
                      </span>
                      <span className="text-[10px] text-foreground-400">{article.date}</span>
                      <span className="text-[10px] text-foreground-400">{article.readTime} 阅读</span>
                    </div>
                    <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}